import React, { useState, useEffect, useRef, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { withTranslation } from 'react-i18next';

import { PrintingModal } from '../../../../utils/complexDocumentConstants';

import { DocumentPanelContainer } from './styles';

import { STATUS_MONITORING, DOCUMENT_PANEL as trans } from '../TranslationValues';
import { metaSearchKeyMappings, stageStatus } from '../../../../utils/constants/statusMonitoring';
import { getChannelTotals } from '../../../../utils/helper';
import { formatDataForApexBarChart, transformMetaSearchKeys } from '../../../../utils/helper';
import { getAllLanguage } from '../../../../utils/services/languageApi';
import { getUser } from '../../../../utils/services/userAPI';
import { channels } from '../../../../utils/constants/statusMonitoring';
import { getSearchMetaKeys, getExclusions } from '../../../../utils/services/statusMonitoringAPI';

const namespaces = ['SEARCHABLE_TABLE', 'DOCUMENT_PANEL', 'EMAIL_DASHBOARD', 'STATUS_MONITORING'];

const EmailPanel = ({
  jobId,
  activeItem,
  t,
  contentType,
  setStatsFilter,
  chartRef,
  totalStats,
  statsFilter,
  emailBarChartRef,
  staticStatsKeys = [],
  selectedSearchFilters,
}) => {
  const keyNames = metaSearchKeyMappings(t);
  const batchStats = activeItem?.batchStats || [];
  const batchType = activeItem?.batchType || '';
  const channelTotals = useMemo(() => getChannelTotals(batchStats), [batchStats]);
  const chartData = {
    series: [totalStats.success, totalStats.failure],
    options: {
      labels: stageStatus(t, 'email', true, false, batchType).map(status => status.key),
      colors: ['#C8E066', '#35346A'],
      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: '50%',
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        floating: false,
        offsetY: 0,
        offsetX: 0,
        formatter: function(val, opts) {
          console.log({ val, opts });
          return (
            '<span style="font-family: Lato"><strong>' +
            val +
            ': ' +
            opts.w.globals.series[opts.seriesIndex] +
            '</strong></span>'
          );
        },
      },
      tooltip: {
        enabled: false,
      },
      chart: {
        events: {
          dataPointSelection: function(event, chartContext, opts) {
            let updatedList = [...stateRef.current];
            const labels = opts.w.config.labels;
            const dataPointIndex = opts.dataPointIndex;

            const isNotSentOrFailure = updatedList.some(
              ({ key, value }) => key === keyNames.status && !labels.includes(value)
            );
            
            if (isNotSentOrFailure) {
              updatedList = updatedList.filter(
                ({ key, value }) => key !== keyNames.status || labels.includes(value)
              );
            }

            if (!updatedList.some(item => item.value === labels[dataPointIndex])) {
              updatedList.push({
                key: keyNames.status,
                value: labels[dataPointIndex],
              });
            } else {
              updatedList = updatedList.filter(item => item.value !== labels[dataPointIndex]);
            }
            stateRef.current = updatedList;
            setStatsFilter(updatedList);
          },
        },
      },
      states: {
        active: {
          allowMultipleDataPointsSelection: true,
          filter: {
            type: 'darken',
          },
        },
      },
    },
  };

  const stateRef = React.useRef(statsFilter);
  useEffect(() => {
    stateRef.current = statsFilter;
  }, [statsFilter]);
  const [languageList, setLanguageList] = useState([]);
  const toggleDataPointPreventRerenderRef = useRef(false);
  const { barChartData, categories } = formatDataForApexBarChart(
    JSON.parse(activeItem?.languageStats)?.email || [],
    t,
    languageList,
    contentType,
    "email",
    batchType
  );

  const [valueDataSet, setValueDataSet] = useState({
    [metaSearchKeyMappings(t).channel]: channels(t),
    [metaSearchKeyMappings(t).status]: stageStatus(t),
  });
  const [metaKeyRefs, setMetaKeyRefs] = useState({});

  const BarData = {
    options: {
      chart: {
        type: 'bar',
        events: {
          dataPointSelection: (event, chartContext, config) => {
            if (!toggleDataPointPreventRerenderRef.current) {
              toggleDataPointPreventRerenderRef.current = true;
              const seriesIndex = config.seriesIndex;
              const dataPointIndex = config.dataPointIndex;
              const selectedLabel = config.w.globals.labels[dataPointIndex];

              let updatedList = [...stateRef.current];

              if (!updatedList.some(item => item.value === selectedLabel)) {
                updatedList.push({
                  key: keyNames.language,
                  value: selectedLabel,
                  seriesIndex: seriesIndex,
                });
              } else {
                updatedList = updatedList.filter(item => item.value !== selectedLabel);
              }

              stateRef.current = updatedList;
              setStatsFilter(stateRef.current);
              chartContext.toggleDataPointSelection(seriesIndex === 0 ? 1 : 0, dataPointIndex);
            } else {
              toggleDataPointPreventRerenderRef.current = false;
            }
          },
        },
        toolbar: {
          show: false,
          tools: {
            download: false,
          },
          export: {},
        },
      },
      colors: ['#C8E066', '#35346A'],
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: false,
        offsetX: -6,
        style: {
          fontSize: '12px',
          colors: ['#000'],
        },
        formatter: function(val) {
          if (val >= 1000000) {
            // more than one million
            return (val / 1000000).toFixed(1) + 'M';
          } else if (val >= 1000) {
            // more than one thousands
            return (val / 1000).toFixed(1) + 'k';
          } else {
            return val;
          }
        },
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['#fff'],
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: function(val, { series, seriesIndex, dataPointIndex, w }) {
            if (val >= 1000000) {
              // more than one millions
              return (val / 1000000).toFixed(1) + 'M';
            } else if (val >= 1000) {
              // more than one thousands
              return (val / 1000).toFixed(1) + 'k';
            } else if (val < 1000) {
              // more than one thousands
              return val.toFixed(0);
            } else {
              return val;
            }
          },
        },
      },
      xaxis: {
        categories: categories,
        labels: {
          formatter: function(val) {
            if (val >= 1000000) {
              // more than one millions
              return (val / 1000000).toFixed(1) + 'M';
            } else if (val >= 1000) {
              // more than one thousands
              return (val / 1000).toFixed(1) + 'k';
            } else {
              return Math.round(val * 10)/10;
            }
          },
        },
      },
      legend: {
        onItemClick: {
          toggleDataSeries: false,
        },
        onItemHover: {
          highlightDataSeries: false,
        },
      },
      states: {
        active: {
          allowMultipleDataPointsSelection: true,
          filter: {
            type: 'darken',
          },
        },
      },
    },
  };

  useEffect(() => {
    if (!jobId) return;

    const fetchAllInfos = async () => {
      try {
        const [metaSearchKeys, users, allLanguages, exclusions] = await Promise.all([
          getSearchMetaKeys(jobId),
          getUser(),
          getAllLanguage(),
          getExclusions(jobId, 'email'),
        ]);

        let valueMappingDataSet;

        if (metaSearchKeys.statusCode === 5002) {
          const metaKey = metaSearchKeys.data?.length
            ? [...new Set([...staticStatsKeys, ...metaSearchKeys.data])]
            : staticStatsKeys;
          const { transformedKeys, referenceKeysObject } = transformMetaSearchKeys(metaKey, t);
          setMetaKeyRefs(referenceKeysObject);
        }



        if (allLanguages.statusCode === 109 && allLanguages.data) {
          const lang = allLanguages.data.map(({ name, code }) => ({ key: name, value: code }));
          valueMappingDataSet = { ...valueDataSet, [metaSearchKeyMappings(t).language]: lang };
          setValueDataSet(prev => ({ ...prev, [metaSearchKeyMappings(t).language]: lang }));
          setLanguageList(lang);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    const fetchLanguages = async () => {
      try {
        const res = await getAllLanguage();
        if (res.statusCode === 109) {
          setLanguageList(res.data.map(({ name, code }) => ({ key: name, value: code })));
        }
      } catch (error) {
        console.error('Error fetching languages:', error);
      }
    };

    contentType === PrintingModal ? fetchAllInfos() : fetchLanguages();
  }, [jobId, contentType]);

  return (
    <>
      {channelTotals.email ? (
        <DocumentPanelContainer>
          <div className="chart-wrapper">
            <p className="chart-title">{t(trans.FAILURE_STATS.key, trans.FAILURE_STATS.value)}</p>
            <Chart
              ref={chartRef}
              type="donut"
              options={activeItem ? chartData.options : {}}
              series={activeItem ? chartData.series : []}
              width={'100%'}
            />
          </div>
          <div className="chart-wrapper">
            <div className="chart-container">
              <p className="chart-title">
                {t(trans.LANGUAGES_STATS.key, trans.LANGUAGES_STATS.value)}
              </p>
              <Chart
                type="bar"
                ref={emailBarChartRef}
                options={activeItem ? BarData.options : {}}
                series={activeItem ? barChartData : []}
                width={'100%'}
                height={categories.length > 3 ? 50 * categories.length : undefined}
              />
            </div>
          </div>
        </DocumentPanelContainer>
      ) : (
        <h4>
          {t(STATUS_MONITORING.NO_RECORDS_FOUND.key, STATUS_MONITORING.NO_RECORDS_FOUND.value)}
        </h4>
      )}
    </>
  );
};

export default withTranslation(namespaces)(EmailPanel);
